import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import { otherAssetsApi } from "@/features/worksheets/api";
import { CrudDialog } from "@/features/worksheets/components/crud-dialog";
import { MoneyField } from "@/features/worksheets/components/money-field";
import { centsToDollars, dollarsToCents } from "@/features/worksheets/money";
import type { OtherAsset } from "@/features/worksheets/types";

interface OtherAssetFormValues {
  description: string;
  title: string;
  currentValue: string;
  loanBalance: string;
}

const validationSchema = Yup.object({
  description: Yup.string().required("Description is required"),
});

function toInitialValues(record: OtherAsset | null): OtherAssetFormValues {
  return {
    description: record?.description ?? "",
    title: record?.title ?? "",
    currentValue: record ? centsToDollars(record.currentValue) : "",
    loanBalance: record ? centsToDollars(record.loanBalance) : "",
  };
}

function toPayload(v: OtherAssetFormValues): Record<string, unknown> {
  return {
    description: v.description,
    title: v.title,
    currentValue: dollarsToCents(v.currentValue),
    loanBalance: dollarsToCents(v.loanBalance),
  };
}

export function OtherAssetDialog({
  caseId,
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  caseId: string;
  record: OtherAsset | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  async function onSave(values: OtherAssetFormValues) {
    const payload = toPayload(values);
    if (record) {
      await otherAssetsApi.update(caseId, record.id, payload);
    } else {
      await otherAssetsApi.create(caseId, payload);
    }
  }

  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Other Asset"
      initialValues={toInitialValues(record)}
      validationSchema={validationSchema}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField
        name="description"
        label="Description"
        labelClassName={FIELD_LABEL}
        placeholder="e.g. Jewelry, artwork"
      />
      <FormField name="title" label="Title" labelClassName={FIELD_LABEL} />
      <div className="grid grid-cols-2 gap-4">
        <MoneyField name="currentValue" label="Current Value" />
        <MoneyField name="loanBalance" label="Loan Balance" />
      </div>
    </CrudDialog>
  );
}
